import { useEffect, useState } from "react";

import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { DRAWING_PREVIEW_MAX_SIZE, DRAWING_TILE_GRID, getDrawingTileRects } from "./fetchDrawingData";
import { getTiledSource, useTiledDrawingImage } from "./tiledDrawing";
import type { Drawing } from "./types";

const l = log.miniMap.utils;

/**
 * Compute the output pixel size for a decode whose longest side is capped to
 * `maxSize` (aspect preserved). Returns the natural size unchanged when it
 * already fits (or when no cap is given).
 */
function fitToMaxSize(
    naturalWidth: number,
    naturalHeight: number,
    maxSize?: number,
): { scale: number; width: number; height: number } {
    let scale = 1;
    if (maxSize) {
        const longest = Math.max(naturalWidth, naturalHeight);
        if (longest > maxSize) {
            scale = maxSize / longest;
        }
    }
    return {
        scale,
        width: Math.max(1, Math.round(naturalWidth * scale)),
        height: Math.max(1, Math.round(naturalHeight * scale)),
    };
}

/**
 * Decode the single-PNG `preview` produced by `fetchDrawingData` into an
 * `ImageBitmap`, optionally downscaled so the longest side is capped to
 * `maxSize` (aspect preserved).
 *
 * The preview is itself capped to `DRAWING_PREVIEW_MAX_SIZE`, so the effective
 * cap here is the smaller of `maxSize` and that — decoding beyond the preview's
 * own resolution would only upscale a low-res image into a large (memory-heavy)
 * bitmap for no visual gain.
 */
async function decodeDrawingPreview(
    preview: Blob,
    naturalWidth: number,
    naturalHeight: number,
    maxSize?: number,
): Promise<ImageBitmap> {
    const cap = Math.min(maxSize ?? Infinity, DRAWING_PREVIEW_MAX_SIZE);
    const { width, height } = fitToMaxSize(naturalWidth, naturalHeight, cap);
    return createImageBitmap(preview, {
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: "high",
    });
}

/**
 * Recompose the grid of PNG tiles produced by `fetchDrawingData` into a single
 * `ImageBitmap`, optionally downscaled so the longest side is capped to
 * `maxSize` (aspect preserved).
 *
 * Each tile is decoded (and, when downscaling, resized) individually and drawn
 * onto an output canvas of the final size, so peak memory stays proportional to
 * the output rather than the full-resolution image. Tile destination rectangles
 * are derived from the same grid math used at encode time so adjacent tiles line
 * up exactly with no seams.
 */
async function decodeDrawingTiles(
    tiles: Blob[],
    naturalWidth: number,
    naturalHeight: number,
    maxSize?: number,
): Promise<ImageBitmap> {
    const { scale, width: outWidth, height: outHeight } = fitToMaxSize(naturalWidth, naturalHeight, maxSize);

    const canvas = new OffscreenCanvas(outWidth, outHeight);
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Failed to create canvas context");
    }

    const rects = getDrawingTileRects(naturalWidth, naturalHeight, DRAWING_TILE_GRID);
    for (let i = 0; i < rects.length && i < tiles.length; i++) {
        const rect = rects[i];
        // Derive destination bounds from scaled full-image coordinates so
        // neighbouring tiles share edges exactly (avoids rounding seams).
        const destX = Math.round(rect.x * scale);
        const destY = Math.round(rect.y * scale);
        const destW = Math.round((rect.x + rect.w) * scale) - destX;
        const destH = Math.round((rect.y + rect.h) * scale) - destY;
        if (destW <= 0 || destH <= 0) {
            continue;
        }

        const tileBitmap = await createImageBitmap(tiles[i], {
            resizeWidth: destW,
            resizeHeight: destH,
            resizeQuality: "high",
        });
        context.drawImage(tileBitmap, destX, destY);
        tileBitmap.close();
    }

    return canvas.transferToImageBitmap();
}

/**
 * Decode a drawing's compressed image {@link Blob} into an `ImageBitmap` on
 * demand.
 *
 * The slice stores drawings as compressed PNG blobs (~MBs) plus their natural
 * pixel dimensions rather than decoded `ImageBitmap`s (~100MB of uncompressed
 * RGBA each) so that many downloaded drawings don't accumulate huge decoded
 * buffers — see `fetchDrawingData`. Each consumer decodes and owns its own
 * bitmap and closes it when the drawing changes or the component unmounts.
 *
 * Pass `maxSize` to decode a downscaled bitmap (longest side capped to
 * `maxSize`, aspect preserved). This also selects the source:
 *
 *   - With a `maxSize` (the mini maps), the small single-PNG `preview` is
 *     decoded. It's available first (before the tiles finish rasterizing) and
 *     is already sized to cover any mini map, so the always-mounted mini map
 *     keeps only a tiny decoded copy resident and never decodes the tiles.
 *   - Without a `maxSize` (the large map), only the full-resolution `tiles` are
 *     decoded — this returns `undefined` until the background tiling finishes so
 *     the large map can keep showing its loading indicator rather than a blurry
 *     preview.
 *
 * `decodeMaxSize` caps the decode below `maxSize` for a consumer that needs a
 * sharp *source* but displays fewer pixels than it — the mobile mini map's
 * zoomed crop. Defaults to `maxSize` (decode whatever was picked at its
 * natural size). See `PreviewSizing` for why the two are separate.
 *
 * The natural size needed to compute the downscale factor comes from the
 * drawing's stored dimensions, so no full-resolution decode is required just to
 * measure it.
 *
 * Returns `undefined` while there is no successful image or while decoding is in
 * flight; callers should render their loading/placeholder state until a bitmap
 * is available.
 */
export function useDrawingImage(
    drawing: Pick<Drawing, "drawingData"> | null | undefined,
    maxSize?: number,
    decodeMaxSize?: number,
): ImageBitmap | undefined {
    // Tiled route (BM-13597): a registered source means the blobs are empty
    // placeholders and the bitmap comes from the server preview tier. The
    // large map (no `maxSize`) gets `undefined` — it paints tiles per
    // viewport via `useTiledLargeMap` instead.
    const tiledSource = getTiledSource(drawing?.drawingData.url);
    const tiledBitmap = useTiledDrawingImage(
        tiledSource,
        maxSize === undefined ? undefined : { tierMaxSize: maxSize, decodeMaxSize },
    );

    const previewState = drawing?.drawingData.previewAsyncState;
    const tilesState = drawing?.drawingData.asyncState;
    const previewData = previewState?.status === AsyncStatus.Success ? previewState.data : undefined;
    const tilesData = tilesState?.status === AsyncStatus.Success ? tilesState.data : undefined;
    const preview = previewData?.preview;
    const tiles = tilesData?.tiles;
    // Natural dimensions are identical in both payloads; prefer whichever is
    // available (they may resolve at different times).
    const naturalWidth = previewData?.width ?? tilesData?.width;
    const naturalHeight = previewData?.height ?? tilesData?.height;

    // The full-resolution large map (no `maxSize`) decodes only the tiles and
    // shows nothing until they've finished rasterizing (its caller renders a
    // loading indicator meanwhile). The mini maps (with a `maxSize`) decode the
    // small preview, which is available first.
    const wantsFullResolution = maxSize === undefined;
    // The PDF route has a single preview rather than tiers, so `maxSize` only
    // ever capped its decode — `decodeMaxSize` is simply a tighter cap here.
    const previewDecodeCap = decodeMaxSize ?? maxSize;
    // Key the decode on the source blob(s) identity (stable until the drawing is
    // re-fetched), not the drawing object, which selectors recreate on every
    // state change.
    const source: Blob | Blob[] | undefined = wantsFullResolution ? tiles : preview;

    // Store the decoded bitmap together with the source it was decoded from so
    // the returned value can be gated on still matching the current source.
    const [decodedEntry, setDecodedEntry] = useState<{ source: Blob | Blob[]; bitmap: ImageBitmap }>();

    useEffect(() => {
        // Tiled drawings never decode blobs here (they're empty placeholders).
        if (tiledSource) {
            return;
        }
        if (!source || naturalWidth === undefined || naturalHeight === undefined) {
            return;
        }
        if (Array.isArray(source) && source.length === 0) {
            return;
        }

        let cancelled = false;
        let bitmap: ImageBitmap | undefined;
        const decode = Array.isArray(source)
            ? decodeDrawingTiles(source, naturalWidth, naturalHeight, maxSize)
            : decodeDrawingPreview(source, naturalWidth, naturalHeight, previewDecodeCap);
        decode
            .then((bmp) => {
                if (cancelled) {
                    // Effect was torn down mid-decode; free the bitmap we just made.
                    bmp.close();
                    return;
                }
                bitmap = bmp;
                setDecodedEntry({ source, bitmap: bmp });
            })
            .catch((error) => {
                l.error("Failed to decode drawing image:", error);
            });

        return () => {
            cancelled = true;
            if (bitmap) {
                bitmap.close();
            }
            // Drop the state reference if it still points at the bitmap we just
            // closed, so we never hand a detached bitmap back to a consumer.
            setDecodedEntry((cur) => (cur?.bitmap === bitmap ? undefined : cur));
        };
    }, [tiledSource, source, naturalWidth, naturalHeight, maxSize, previewDecodeCap]);

    // Tiled route resolves via its own hook (already source-gated there).
    if (tiledSource) {
        return tiledBitmap;
    }

    // Only expose the bitmap once it matches the *current* source and has not
    // been released. During a drawing switch (or a preview → tiles upgrade) the
    // state briefly holds a bitmap decoded from a different source; returning it
    // would draw a stale image. The `width > 0` check is a final safety net: a
    // closed `ImageBitmap` reports a width of 0, so this never hands a detached
    // bitmap to `drawImage`.
    if (decodedEntry && decodedEntry.source === source && decodedEntry.bitmap.width > 0) {
        return decodedEntry.bitmap;
    }
    return undefined;
}
